'use client';

import { Env } from '@/api/auth/env';
import { createContext, useContext, useMemo } from 'react';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';

export type EnvironmentContextState = {
  apiUrl: string;
  authApiUrl: string;
  oauthApiUrl: string | undefined;
  isAuth0: boolean;
  enableTranslations: boolean;
  enableConversations: boolean;
  enableLLMIssuesV2: boolean;
  isDev: boolean;
  appTitle?: string;
  login_flows_ary: ('microsoft' | 'google' | 'okta' | 'password')[];
  deploymentMode: 'self-hosted' | 'saas';
  authMode: 'auth0' | string;
  enableBetaFeatures: boolean;
  enableAddMember: boolean;
  customIntentConfig?: Record<string, string>;
  defaultAggregation: 'interaction' | 'conversation' | undefined;
  enableAiSummary: boolean;
  auth0domain: string;
  auth0clientId: string;
  auth0audience: string;
  auth0scope: string;
  missingProcessingWrappingText?: string;
  gitRef?: string;
  enableInteractionsDetailsAccessControl: boolean;
  interactionsDetailsAccessControlMode: 'reason' | 'disabled';
  interactionsDetailsAccessControlRoles: string[];
  enableLLMIssuesHiding: boolean;
  gtmId?: string;
  ldapEnabled: boolean;
  enableSubCategories: boolean;
  enableOutOfScope?: boolean;
  enableCustomActivationRate?: boolean;
  enableCohorts: boolean;
  helmChartVersion?: string;
  performanceMode: boolean;
  faviconUrl?: string;
  showFaviconSidebar?: boolean;
  enableCustomVariables?: boolean;
  enableUserAnonymization: boolean;
  enableRemoteAccess: boolean;
  newUrl?: string;
  legacyUrl?: string;
  enableAdminExportLogs: boolean;
  showTanstackDevtools: boolean;
};

export const EnvironmentContext = createContext<EnvironmentContextState | null>(null);

export const EnvironmentProvider = ({ children, environment }: { children: React.ReactNode; environment: Env }) => {
  const augmentedEnvironment = useMemo(() => {
    return {
      gitRef: environment.GIT_REF,
      deploymentMode: environment.DEPLOYMENT_MODE as 'self-hosted' | 'saas',
      appTitle: environment.NEXT_PUBLIC_APP_TITLE,
      apiUrl: environment.NEXT_PUBLIC_API_URL,
      authApiUrl: environment.NEXT_PUBLIC_AUTH_API_URL,
      oauthApiUrl: environment.NEXT_PUBLIC_OAUTH_API_URL,
      authMode: environment.NEXT_PUBLIC_AUTH_MODE,
      isAuth0: environment.NEXT_PUBLIC_AUTH_MODE === 'auth0',
      isDev: environment.NEXT_PUBLIC_SENTRY_ENVIRONMENT === 'dev',
      enableTranslations: environment.NEXT_PUBLIC_ENABLE_TRANSLATIONS === 'true',
      enableConversations: environment.NEXT_PUBLIC_ENABLE_CONVERSATIONS === 'true',
      enableLLMIssuesV2: environment.NEXT_PUBLIC_ENABLE_LLM_ISSUES_V2 === 'true',
      login_flows_ary: (environment.LOGIN_MODES ?? 'password').split(',') as (
        'microsoft' | 'google' | 'okta' | 'password'
      )[],
      enableBetaFeatures: environment.NEXT_PUBLIC_ENABLE_BETA_FEATURES === 'true',
      enableAddMember: environment.NEXT_PUBLIC_ENABLE_ADD_MEMBER === 'true',
      customIntentConfig: environment.CUSTOM_INTENT_CONFIG,
      defaultAggregation: environment.NEXT_PUBLIC_DEFAULT_AGGREGATION as AggregateBy | undefined,
      enableAiSummary: environment.NEXT_PUBLIC_ENABLE_AI_SUMMARY === 'true',
      auth0domain: environment.NEXT_PUBLIC_AUTH0_DOMAIN,
      auth0clientId: environment.NEXT_PUBLIC_AUTH0_CLIENT_ID,
      auth0audience: environment.NEXT_PUBLIC_AUTH0_AUDIENCE,
      auth0scope: environment.NEXT_PUBLIC_AUTH0_SCOPE,
      missingProcessingWrappingText: environment.MISSING_PROCESSING_WRAPPING_TEXT,
      enableInteractionsDetailsAccessControl:
        environment.NEXT_PUBLIC_ENABLE_INTERACTIONS_DETAILS_ACCESS_CONTROL === 'true',
      interactionsDetailsAccessControlMode:
        environment.NEXT_PUBLIC_INTERACTIONS_DETAILS_ACCESS_CONTROL_MODE ?? 'disabled',
      interactionsDetailsAccessControlRoles: (
        environment.NEXT_PUBLIC_INTERACTIONS_DETAILS_ACCESS_CONTROL_ROLES ?? 'admin, member, viewer'
      )
        .split(',')
        .map((role) => role.trim()),
      enableLLMIssuesHiding: environment.NEXT_PUBLIC_ENABLE_LLM_ISSUE_HIDING === 'true',
      gtmId: environment.NEXT_PUBLIC_GTM_ID,
      ldapEnabled: environment.NEXT_PUBLIC_LDAP_ENABLED === 'true',
      enableSubCategories: environment.NEXT_PUBLIC_ENABLE_SUB_CATEGORIES === 'true',
      enableOutOfScope: environment.NEXT_PUBLIC_ENABLE_OUT_OF_SCOPE === 'true',
      enableCustomActivationRate: environment.NEXT_PUBLIC_ENABLE_CUSTOM_ACTIVATION_RATE === 'true',
      enableCohorts: environment.NEXT_PUBLIC_ENABLE_COHORTS !== 'false',
      helmChartVersion: environment.HELM_CHART_VERSION,
      performanceMode: environment.NEXT_PUBLIC_PERFORMANCE_MODE === 'true',
      faviconUrl: environment.NEXT_PUBLIC_APP_FAVICON,
      showFaviconSidebar: environment.NEXT_PUBLIC_SHOW_FAVICON_SIDEBAR === 'true',
      enableCustomVariables: environment.NEXT_PUBLIC_ENABLE_CUSTOM_VARIABLES === 'true',
      enableUserAnonymization: environment.NEXT_PUBLIC_ENABLE_USER_ANONYMIZATION === 'true',
      enableRemoteAccess: environment.NEXT_PUBLIC_ENABLE_REMOTE_ACCESS === 'true',
      newUrl: environment.NEXT_URL,
      legacyUrl: environment.NEXT_LEGACY_URL,
      enableAdminExportLogs: environment.NEXT_PUBLIC_ENABLE_ADMIN_EXPORT_LOGS === 'true',
      showTanstackDevtools: environment.SHOW_TANSTACK_DEVTOOLS === 'true',
    };
  }, [environment]);

  return (
    <EnvironmentContext.Provider value={augmentedEnvironment}>
      {children}
      {/*<NuqsAdapter>{children}</NuqsAdapter>*/}
    </EnvironmentContext.Provider>
  );
};

export const useEnvironmentContext = () => {
  const context = useContext(EnvironmentContext);

  if (!context) {
    throw new Error('useEnvironmentContext must be used within a EnvironmentProvider');
  }
  return context;
};
